import { useMemo, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { encounterQueryKeys, encountersApi } from "../../shared/api/encounters";
import { appointmentQueryKeys, appointmentsApi } from "../../shared/api/appointments";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import { isCalled, orderWaiting } from "./waitingOrder";
import {
  DOCTOR_REQUIRED_HINT,
  ROOM_REQUIRED_HINT,
  hasDoctor,
  hasRoom,
  startEncounterErrorMessage,
} from "../../shared/encounterStart";
import { useRoomRequired } from "../../shared/hooks/useRoomRequired";
import {
  formatDate,
  formatPhone,
  formatTime,
  genderLabel,
  isoDate,
} from "../../shared/format";
import type {
  EncounterListFilter,
  EncounterStatus,
  EncounterSummary,
} from "../../shared/types/encounter";
import { PageHeader } from "../../shared/ui/PageHeader";
import { FilterBar } from "../../shared/ui/FilterBar";
import { DateRangePicker } from "../../shared/ui/DateRangePicker";
import { describeRange } from "../../shared/dateRange";
import { DataTable, type Column } from "../../shared/ui/DataTable";
import { Pagination } from "../../shared/ui/Pagination";
import { StatusPill } from "../../shared/ui/StatusPill";
import { PatientLink } from "../../shared/ui/PatientLink";
import {
  BulkDeleteBar,
  runBulkDelete,
  useRowSelection,
} from "../../shared/ui/bulkSelect";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, FileSpreadsheet } from "lucide-react";
import { apiError } from "../../shared/api/client";
import { EncounterRowActions } from "./components/EncounterRowActions";
import {
  EncounterResultDialog,
  usePrefetchEncounterResult,
} from "./components/EncounterResultDialog";
import { STATUS_LABEL, STATUS_TONE } from "./encounterStatus";
import {
  downloadEncountersXlsx,
  encounterExportFileName,
  fetchAllEncounters,
  formatRegistrationNo,
} from "./encounterExport";

const PAGE_SIZE = 25;
type Tab = "queue" | "all";

/**
 * "Xem kết quả" của một lần khám đã hoàn thành — mở hộp thoại kết quả ngay trên danh sách, như
 * SalePro. Click được chặn tại chỗ để không nổi lên dòng (dòng điều hướng sang màn khám).
 * Rê chuột / focus vào đã nạp sẵn dữ liệu, để lúc bấm hộp thoại mở với nội dung ngay.
 */
function ResultLink({ onOpen, onPrefetch }: { onOpen: () => void; onPrefetch: () => void }) {
  const stop = (ev: MouseEvent | KeyboardEvent) => ev.stopPropagation();
  return (
    <span onClick={stop} onKeyDown={stop}>
      <button
        type="button"
        data-testid="encounter-result"
        className="text-sm font-medium text-primary hover:underline"
        onClick={onOpen}
        onMouseEnter={onPrefetch}
        onFocus={onPrefetch}
      >
        Xem kết quả
      </button>
    </span>
  );
}

/** Small progress marker used in the queue table (Vitals / Dx / Rx). */
function ProgressBadge({ done, children, title }: { done: boolean; children: ReactNode; title: string }) {
  return (
    <Badge variant={done ? "success" : "muted"} title={title} className="tabular-nums">
      {children}
    </Badge>
  );
}

export function EncounterListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  // Tab + bộ lọc tab Lịch sử nằm trên URL (kiểu SalePro admin): reload hay quay về từ một lần
  // khám vẫn đúng tab, đúng bộ lọc. Vắng mặt = tab hàng đợi, không lọc.
  const { filters, set, reset } = useUrlFilters({
    tab: param.oneOf(["queue", "all"] as const, "queue"),
    q: param.string(),
    status: param.oneOf(Object.keys(STATUS_LABEL) as EncounterStatus[]),
    doctorId: param.string(),
    from: param.isoDate(),
    to: param.isoDate(),
    page: param.int(),
  });
  const { tab, q, status, doctorId, from: fromDate, to: toDate, page } = filters;
  const debouncedQ = useDebounce(q, 250);
  const setTab = (t: Tab) => set({ tab: t });
  const clearFilters = () => reset(["q", "status", "doctorId", "from", "to"]);

  // Hộp thoại "Kết quả của: …" (cột Kết quả, tab Lịch sử khám).
  const [resultOf, setResultOf] = useState<EncounterSummary | null>(null);
  const prefetchResult = usePrefetchEncounterResult();

  // Multi-select delete (History tab only).
  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Refusal from "Bắt đầu khám" (#18602) — shown on the queue tab, where the button lives.
  const [startError, setStartError] = useState<string | null>(null);

  const filter: EncounterListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      status: status || null,
      doctorId: doctorId || null,
      fromDate: fromDate || null,
      toDate: toDate || null,
    }),
    [debouncedQ, status, doctorId, fromDate, toDate]
  );

  const doctors = useQuery({
    queryKey: appointmentQueryKeys.doctors(),
    queryFn: () => appointmentsApi.listDoctors(),
    enabled: tab === "all",
  });

  const queue = useQuery({
    queryKey: encounterQueryKeys.queue(),
    queryFn: () => encountersApi.queue(),
    enabled: tab === "queue",
  });

  // Checked-in appointments not yet promoted to encounter — start-encounter affordance
  const today = isoDate(new Date());
  const checkedIn = useQuery({
    queryKey: appointmentQueryKeys.forDate(today),
    queryFn: () => appointmentsApi.forDate(today),
    enabled: tab === "queue",
    // Called patient first, and marked (#18602).
    select: (data) => orderWaiting(data.filter((a) => a.status === "CHECKED_IN")),
  });

  // #19665 — Phòng = Bắt buộc: a visit with no room cannot be STARTED here (one already in exam
  // can still be continued — the server allows that too).
  const roomRequired = useRoomRequired();

  const startEncounter = useMutation({
    mutationFn: async (appointment: {
      id: string;
      patientId: string;
      patientName: string;
      patientPhone: string | null;
      doctorId: string;
      doctorName: string;
    }) =>
      encountersApi.createFromAppointment({
        appointmentId: appointment.id,
        patientId: appointment.patientId,
        patientName: appointment.patientName,
        patientCode: null,
        patientPhone: appointment.patientPhone,
        patientGender: null,
        patientAge: null,
        doctorId: appointment.doctorId,
        doctorName: appointment.doctorName,
      }),
    onMutate: () => setStartError(null),
    onSuccess: (encounter) => {
      qc.invalidateQueries({ queryKey: encounterQueryKeys.queue() });
      navigate(`/encounters/${encounter.id}`);
    },
    // #18602 — the server refuses a visit that is already finished (409). Say so, and reload the
    // block so the stale row that invited the click goes away.
    onError: (e) => {
      setStartError(startEncounterErrorMessage(e));
      qc.invalidateQueries({ queryKey: appointmentQueryKeys.all });
      qc.invalidateQueries({ queryKey: encounterQueryKeys.all });
    },
  });

  const all = useQuery({
    queryKey: encounterQueryKeys.list(filter, page),
    queryFn: () =>
      encountersApi.list(filter, { page, size: PAGE_SIZE, sort: "startedAt,desc" }),
    enabled: tab === "all",
    placeholderData: (prev) => prev,
  });

  // Xuất Excel (kiểu SalePro): một nút, xuất MỌI dòng khớp bộ lọc đang áp — đi qua từng trang của
  // cùng bộ lọc với bảng, không chỉ 25 dòng đang hiển thị. Lỗi hiện ở cùng ô với lỗi xoá hàng loạt.
  const [exporting, setExporting] = useState(false);
  async function exportExcel() {
    setExporting(true);
    setError(null);
    try {
      const rows = await fetchAllEncounters(filter, (f, pageable) =>
        encountersApi.list(f, pageable)
      );
      downloadEncountersXlsx(rows, encounterExportFileName(fromDate, toDate));
    } catch (e) {
      setError(`Xuất Excel thất bại: ${apiError(e).detail}`);
    } finally {
      setExporting(false);
    }
  }

  const queueColumns: Column<EncounterSummary>[] = [
    {
      key: "patient",
      header: "Bệnh nhân",
      render: (e) => (
        <div>
          <PatientLink id={e.patientId} name={e.patientName} className="font-semibold text-foreground" />
          <div className="font-mono text-xs text-muted-foreground">
            {e.patientCode && `${e.patientCode} · `}
            {e.patientGender && `${genderLabel[e.patientGender]} · `}
            {e.patientAge != null && `${e.patientAge}T · `}
            {e.patientPhone && formatPhone(e.patientPhone)}
          </div>
        </div>
      ),
    },
    {
      key: "complaint",
      header: "Lý do khám",
      render: (e) =>
        e.chiefComplaint ? (
          <span>{e.chiefComplaint}</span>
        ) : (
          <span className="text-muted-foreground/60">Chưa ghi</span>
        ),
    },
    { key: "doctor", header: "Bác sĩ", width: 200, render: (e) => e.doctorName },
    {
      key: "started",
      header: "Bắt đầu",
      width: 80,
      render: (e) => <span className="font-mono text-xs">{formatTime(e.startedAt)}</span>,
    },
    {
      key: "progress",
      header: "Tiến độ",
      width: 200,
      render: (e) => (
        <div className="flex gap-1.5">
          <ProgressBadge done={e.hasVitals} title="Sinh hiệu">
            Vitals
          </ProgressBadge>
          <ProgressBadge done={!!e.diagnosisSummary} title="Chẩn đoán">
            Dx
          </ProgressBadge>
          <ProgressBadge done={e.hasPrescription} title="Đơn thuốc">
            Rx
          </ProgressBadge>
        </div>
      ),
    },
  ];

  // "Chưa hoàn tất từ ngày trước" (#18580) reuses the queue columns but shows the start DATE (not
  // just the time) so it's clear which prior day the exam is from.
  const overdueColumns: Column<EncounterSummary>[] = queueColumns.map((c) =>
    c.key === "started"
      ? {
          ...c,
          header: "Bắt đầu",
          width: 130,
          render: (e) => (
            <div>
              <div className="font-mono text-xs">{formatDate(e.startedAt)}</div>
              <div className="text-xs text-muted-foreground">{formatTime(e.startedAt)}</div>
            </div>
          ),
        }
      : c
  );

  // Split the in-progress queue so today's exams stay in "Đang khám" and exams started on an
  // earlier day surface in their own group — without changing the business status (#18580).
  const { todayQueue, overdueQueue } = useMemo(() => {
    const rows = queue.data ?? [];
    const todayRows: EncounterSummary[] = [];
    const overdueRows: EncounterSummary[] = [];
    for (const e of rows) {
      if (e.startedAt && isoDate(new Date(e.startedAt)) < today) overdueRows.push(e);
      else todayRows.push(e);
    }
    return { todayQueue: todayRows, overdueQueue: overdueRows };
  }, [queue.data, today]);

  const allColumns: Column<EncounterSummary>[] = [
    {
      // Cột đầu như SalePro: số thứ tự tiếp đón của lịch hẹn nguồn, in `STT0213`. Trống khi lần
      // khám không qua lịch hẹn hoặc lịch hẹn không được cấp số (dữ liệu SalePro trước 07/2025).
      key: "stt",
      header: "Số thứ tự",
      width: 100,
      render: (e) =>
        e.registrationNo != null ? (
          <span className="font-mono text-xs">{formatRegistrationNo(e.registrationNo)}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
    },
    {
      key: "patient",
      header: "Bệnh nhân",
      width: 220,
      render: (e) => (
        <div>
          <PatientLink id={e.patientId} name={e.patientName} className="font-semibold text-foreground" />
          {e.patientCode && <div className="font-mono text-xs text-muted-foreground">{e.patientCode}</div>}
        </div>
      ),
    },
    {
      key: "when",
      header: "Khám lúc",
      width: 110,
      render: (e) => (
        <div>
          <div className="font-mono text-xs">{formatDate(e.startedAt)}</div>
          <div className="text-xs text-muted-foreground">{formatTime(e.startedAt)}</div>
        </div>
      ),
    },
    {
      key: "reason",
      header: "Lý do khám",
      render: (e) =>
        e.chiefComplaint ? (
          <span className="text-sm text-foreground">{e.chiefComplaint}</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
    },
    { key: "doctor", header: "Bác sĩ", width: 210, render: (e) => e.doctorName },
    {
      key: "dx",
      header: "Chẩn đoán",
      render: (e) =>
        e.diagnosisSummary ?? <span className="text-muted-foreground/60">—</span>,
    },
    {
      // Cột "Kết quả" như SalePro: lần khám Hoàn thành (ĐÃ KÝ) có "Xem kết quả" mở hộp thoại
      // `EncounterResultDialog`. Đang thực hiện thì "Chưa có"; đã huỷ thì không bao giờ có kết quả.
      key: "result",
      header: "Kết quả",
      width: 110,
      render: (e) =>
        e.status === "SIGNED" ? (
          <ResultLink onOpen={() => setResultOf(e)} onPrefetch={() => prefetchResult(e)} />
        ) : e.status === "IN_PROGRESS" ? (
          <span className="text-muted-foreground/60">Chưa có</span>
        ) : (
          <span className="text-muted-foreground/60">—</span>
        ),
    },
    {
      key: "status",
      header: "Trạng thái",
      width: 120,
      render: (e) => (
        <StatusPill tone={STATUS_TONE[e.status]}>
          {STATUS_LABEL[e.status]}
        </StatusPill>
      ),
    },
    {
      key: "actions",
      header: <span className="sr-only">Thao tác</span>,
      width: 110,
      stickyRight: true,
      render: (e) => <EncounterRowActions encounter={e} />,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Khám bệnh"
        description="Hàng chờ bệnh nhân đã check-in + lịch sử các lần khám đã hoàn thành."
        breadcrumbs={[{ label: "Trang chủ", to: "/dashboard" }, { label: "Khám bệnh" }]}
        actions={
          <div className="flex items-center gap-2">
            {tab === "all" && (
              <Button
                type="button"
                variant="secondary"
                disabled={exporting || !all.data || all.data.totalElements === 0}
                onClick={() => void exportExcel()}
                title="Xuất mọi lần khám khớp bộ lọc đang áp"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {exporting ? "Đang xuất…" : "Xuất Excel"}
              </Button>
            )}
            <Button asChild variant="secondary">
              <Link to="/appointments/day">Lịch tiếp đón</Link>
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-1 border-b border-border">
        <button
          type="button"
          className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            tab === "queue"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("queue")}
        >
          Hàng chờ
          {queue.data && queue.data.length > 0 && (
            <Badge variant="success">{queue.data.length}</Badge>
          )}
          {checkedIn.data && checkedIn.data.length > 0 && (
            <Badge variant="muted">+{checkedIn.data.length}</Badge>
          )}
        </button>
        <button
          type="button"
          className={`-mb-px inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
            tab === "all"
              ? "border-primary text-foreground"
              : "border-transparent text-muted-foreground hover:text-foreground"
          }`}
          onClick={() => setTab("all")}
        >
          Lịch sử khám
        </button>
      </div>

      {tab === "queue" && (
        <>
          {startError && (
            <div
              role="alert"
              className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {startError}
            </div>
          )}
          {checkedIn.data && checkedIn.data.length > 0 && (
            <section className="mb-4 rounded-md border border-primary bg-primary/5 p-4">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-primary">
                  Đã check-in — chờ bắt đầu khám
                </h2>
                <span className="text-xs text-muted-foreground">
                  {checkedIn.data.length} bệnh nhân
                </span>
              </div>
              <div className="grid gap-2">
                {checkedIn.data.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-2.5 rounded-md bg-card px-3.5 py-2.5"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <PatientLink id={a.patientId} name={a.patientName} className="block font-semibold text-foreground" />
                        {isCalled(a) && (
                          <Badge variant="success" data-testid="called-badge">
                            Đã gọi{a.roomNo != null ? ` · STT ${a.roomNo}` : ""}
                          </Badge>
                        )}
                      </div>
                      <div className="font-mono text-xs text-muted-foreground">
                        {formatTime(a.startAt)} · {a.doctorName}
                        {a.serviceName && ` · ${a.serviceName}`}
                      </div>
                    </div>
                    {!hasDoctor(a) ? (
                      // #18899 — no doctor, no request: the doctor is assigned on Tiếp đón, so send
                      // staff there instead of letting the server refuse.
                      <Button variant="outline" asChild title={DOCTOR_REQUIRED_HINT}>
                        <Link to="/reception" data-testid="needs-doctor">
                          Cần chọn bác sĩ
                          <ArrowRight aria-hidden />
                        </Link>
                      </Button>
                    ) : roomRequired && !hasRoom(a) && !a.inExam ? (
                      // #19665 — the room is assigned on Tiếp đón ("Phân phòng"), so send staff
                      // there, exactly like the doctor case above.
                      <Button variant="outline" asChild title={ROOM_REQUIRED_HINT}>
                        <Link to="/reception" data-testid="needs-room">
                          Cần phân phòng
                          <ArrowRight aria-hidden />
                        </Link>
                      </Button>
                    ) : (
                    <Button
                      type="button"
                      disabled={startEncounter.isPending}
                      onClick={() =>
                        startEncounter.mutate({
                          id: a.id,
                          patientId: a.patientId,
                          patientName: a.patientName,
                          patientPhone: a.patientPhone,
                          doctorId: a.doctorId,
                          doctorName: a.doctorName,
                        })
                      }
                    >
                      Bắt đầu khám
                      <ArrowRight aria-hidden />
                    </Button>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {overdueQueue.length > 0 && (
            <section className="rounded-md border border-amber-300 bg-amber-50 p-4">
              <div className="mb-3">
                <h2 className="text-sm font-semibold text-amber-800">
                  Chưa hoàn tất từ ngày trước ({overdueQueue.length})
                </h2>
                <p className="mb-0 mt-1 text-xs text-amber-700">
                  Lượt khám bắt đầu từ ngày trước nhưng chưa hoàn tất — vẫn giữ trạng thái nghiệp vụ
                  "Đang khám". Bấm để tiếp tục xử lý.
                </p>
              </div>
              <DataTable
                ariaLabel="Chưa hoàn tất từ ngày trước"
                columns={overdueColumns}
                data={overdueQueue}
                onRowClick={(e) => navigate(`/encounters/${e.id}`)}
                density="compact"
              />
            </section>
          )}

          <section className="rounded-md border border-border bg-card p-4">
            <div className="mb-3">
              <h2 className="text-sm font-semibold text-foreground">Đang khám</h2>
            </div>
            <DataTable
              ariaLabel="Hàng chờ khám"
              columns={queueColumns}
              data={todayQueue}
              loading={queue.isLoading}
              error={queue.error}
              onRowClick={(e) => navigate(`/encounters/${e.id}`)}
              empty={{
                title: "Hiện không có ai đang khám",
                description:
                  "Bệnh nhân sẽ xuất hiện ở đây khi lễ tân bấm 'Nhận khám' trên lịch hẹn của họ.",
              }}
              density="compact"
            />
          </section>
        </>
      )}

      {tab === "all" && (
        <>
          <FilterBar
            search={{
              value: q,
              onChange: (v) => set({ q: v }),
              placeholder: "Tìm bệnh nhân / lý do khám / bác sĩ",
              wide: true,
            }}
            onClearAll={clearFilters}
            fields={[
              {
                key: "status",
                label: "Trạng thái",
                value: status ? STATUS_LABEL[status] : null,
                onClear: () => set({ status: "" }),
                render: (id) => (
                  <Select
                    value={status || "ALL"}
                    onValueChange={(v) =>
                      set({ status: v === "ALL" ? "" : (v as EncounterStatus) })
                    }
                  >
                    <SelectTrigger id={id} aria-label="Lọc trạng thái">
                      <SelectValue placeholder="Mọi trạng thái" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Mọi trạng thái</SelectItem>
                      {(Object.keys(STATUS_LABEL) as EncounterStatus[]).map((s) => (
                        <SelectItem key={s} value={s}>
                          {STATUS_LABEL[s]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ),
              },
              {
                key: "doctor",
                label: "Bác sĩ",
                value: doctorId
                  ? (doctors.data?.find((d) => d.id === doctorId)?.name ?? "Đã chọn")
                  : null,
                onClear: () => set({ doctorId: "" }),
                render: (id) => (
                  <Select
                    value={doctorId || "ALL"}
                    onValueChange={(v) => set({ doctorId: v === "ALL" ? "" : v })}
                  >
                    <SelectTrigger id={id} aria-label="Lọc bác sĩ">
                      <SelectValue placeholder="Mọi bác sĩ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">Mọi bác sĩ</SelectItem>
                      {doctors.data?.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ),
              },
              {
                key: "range",
                label: "Khám lúc",
                value: describeRange(fromDate, toDate),
                onClear: () => set({ from: "", to: "" }),
                render: (id) => (
                  <DateRangePicker
                    id={id}
                    from={fromDate}
                    to={toDate}
                    onChange={({ from, to }) => set({ from, to })}
                  />
                ),
              },
            ]}
          />

          {error && (
            <div className="mb-3 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}

          <BulkDeleteBar
            count={sel.count}
            noun="hồ sơ khám"
            pending={bulkPending}
            onClear={sel.clear}
            onDelete={async () => {
              const reason = prompt(
                "Xoá vĩnh viễn các hồ sơ khám chưa ký. Hồ sơ ĐÃ KÝ sẽ bị từ chối.\n\nLý do xoá:"
              );
              if (reason === null || !reason.trim()) return;
              setBulkPending(true);
              setError(null);
              const { failed } = await runBulkDelete([...sel.selectedIds], (id) =>
                encountersApi.remove(id, reason.trim())
              );
              sel.clear();
              qc.invalidateQueries({ queryKey: encounterQueryKeys.all });
              if (failed.length) setError(`${failed.length} mục không xoá được.`);
              setBulkPending(false);
            }}
          />

          <DataTable
            ariaLabel="Lịch sử khám"
            columns={allColumns}
            data={all.data?.content}
            loading={all.isLoading}
            error={all.error}
            selectedIds={sel.selectedIds}
            onSelectionChange={sel.setSelectedIds}
            onRowClick={(e) => navigate(`/encounters/${e.id}`)}
            empty={{
              title: "Không có lần khám nào khớp bộ lọc",
              description: "Thử mở rộng khoảng ngày hoặc bỏ bớt bộ lọc.",
            }}
            footer={
              <Pagination
                page={page}
                totalPages={all.data?.totalPages ?? 0}
                totalElements={all.data?.totalElements ?? 0}
                pageSize={PAGE_SIZE}
                onChange={(p) => set({ page: p })}
              />
            }
          />
          <EncounterResultDialog encounter={resultOf} onClose={() => setResultOf(null)} />
        </>
      )}
    </div>
  );
}
